import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import TextField from 'core/components/validatedForm/TextField'
import RadioFields from 'core/components/validatedForm/radio-fields'
import Text from 'core/elements/Text'
import PlusButton from 'core/elements/button/PlusButton'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import { useCallback } from 'react'

const useStyles = makeStyles<Theme>((theme) => ({
  table: {
    display: 'grid',
    gridAutoRows: 'auto',
    border: `1px solid ${theme.components.table.border}`,
    gap: 0,
  },
  headerRow: {
    display: 'grid',
    gridTemplateColumns: '22% 18% 10% 10% 10% 10% 10% 10%',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  physicalNetworkLabelHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'baseline',
  },
  sameLine: {
    display: 'grid',
    gap: 8,
    alignItems: 'center',
    gridTemplateColumns: 'max-content max-content auto',
    marginLeft: theme.spacing(-1.5),
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '22% 18% 10% 10% 10% 10% 10% 10%',
    gridColumnGap: theme.spacing(1),
    alignItems: 'center',
    borderTop: `1px solid ${theme.components.table.border}`,
    padding: theme.spacing(1.5, 1),
  },
  networkInterface: {
    gap: 0,
    width: '90%',
  },
  physicalNetworkLabel: {
    gap: 0,
    width: '80%',
  },
  tableFooter: {
    display: 'grid',
    height: '36px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderTop: `1px solid ${theme.components.table.border}`,
    padding: theme.spacing(1.5, 2),
  },
  remove: {
    color: theme.components.badge.primary.color,
    cursor: 'pointer',
  },
  disabled: {
    color: theme.components.button.primary.disabledBackground,
  },
}))

interface HostNetworkInterfacesTableProps {
  params: any
  config: any
  idx: number
  isUsedHostConfig: boolean
  updateNetworkInterface: (value: any, property: string, ifaceIdx: number) => void
  removeNetworkInterface: (ifaceIdx: number) => void
  addNetworkInterface: () => void
  virtualNetworkLabelValidator: any
  validLabelString: any
  existingNetworkInterfaces: any
}

export default function HostNetworkInterfacesTable({
  params,
  config,
  idx,
  isUsedHostConfig,
  updateNetworkInterface,
  removeNetworkInterface,
  addNetworkInterface,
  virtualNetworkLabelValidator,
  validLabelString,
  existingNetworkInterfaces,
}: HostNetworkInterfacesTableProps) {
  const classes = useStyles()
  const getUniqueNetworkInterfaceValidator = useCallback(
    (interfaceIdx) =>
      customValidator((value) => {
        const physnetExists =
          config?.networkInterfaces?.find(
            (i, idx) => interfaceIdx !== idx && i.physnet?.toLowerCase() === value?.toLowerCase(),
          )?.physnet !== value?.toLowerCase()
        return Boolean(physnetExists)
      }, 'Network Interface name must be unique'),
    [config?.networkInterfaces],
  )

  const getUniquePhysicalNetworkLabelValidator = useCallback(
    (configIndex, interfaceIdx) =>
      customValidator((value) => {
        const config = params.hostNetworkConfigs?.[configIndex]
        const matchingLabelExists = config.networkInterfaces.find(
          (iface, idx) =>
            idx !== interfaceIdx &&
            iface.physicalNetworkLabel?.toLowerCase() === value?.toLowerCase(),
        )
        return !Boolean(matchingLabelExists)
      }, 'Labels must be unique per network interface on a Host Network Configuration'),
    [params.hostNetworkConfigs],
  )

  return (
    <div className={classes.table}>
      <div className={classes.headerRow}>
        <Text variant="caption2">Network Interface * </Text>
        <Text component="div" variant="caption2" className={classes.physicalNetworkLabelHeader}>
          Physical <br />
          <span className={classes.sameLine}>
            Network Label{' '}
            <HintTooltip message="A descriptive, human-readable label to identify and organize this physical network interface across your hosts. Examples: Production-Network, Management-VLAN, Storage-Backend." />
          </span>
        </Text>
        <Text variant="caption2">Management *</Text>
        <Text variant="caption2">VM Console *</Text>
        <Text variant="caption2">Image Library I/O *</Text>
        <Text variant="caption2">Virtual Network Tunnels *</Text>
        <Text variant="caption2">Host Liveness Checks *</Text>
      </div>
      <div>
        {config?.networkInterfaces?.map((iface, ifaceIdx) => {
          const isExistingNetworkInterface = existingNetworkInterfaces?.find(
            (existingInterface, idx) =>
              existingInterface.physnet === iface.physnet && idx === ifaceIdx,
          )
          const isPhysicalNetworkLabelRequired =
            (iface.tunnel && params.segmentationTechnology === 'vlan') ||
            (isUsedHostConfig && iface?.physnetName === '')

          return (
            <div className={classes.tableRow} key={ifaceIdx}>
              <TextField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.physnet`}
                label=""
                onChange={(value) => {
                  // Do not allow spaces
                  const strippedValue = value.replace(/\s/g, '')
                  updateNetworkInterface(strippedValue, 'physnet', ifaceIdx)
                }}
                value={iface.physnet}
                placeholder="Physical Interface Name"
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                className={classes.networkInterface}
                validations={[getUniqueNetworkInterfaceValidator(ifaceIdx)]}
                required
              />
              <TextField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.physicalNetworkLabel`}
                label=""
                onChange={(value) => {
                  // Do not allow spaces
                  const strippedValue = value.replace(/\s/g, '')
                  updateNetworkInterface(strippedValue, 'physicalNetworkLabel', ifaceIdx)
                }}
                value={iface.physicalNetworkLabel}
                placeholder={
                  isPhysicalNetworkLabelRequired ? 'Label (required)' : 'Label (optional)'
                }
                disabled={
                  (isUsedHostConfig && iface?.physnetName !== '') || isExistingNetworkInterface
                }
                required={isPhysicalNetworkLabelRequired}
                validations={
                  // If the host config is already in use and existing label, don't validate the label
                  isUsedHostConfig && iface?.physnetName !== ''
                    ? null
                    : iface.tunnel && params.segmentationTechnology === 'vlan'
                    ? [
                        validLabelString,
                        virtualNetworkLabelValidator(isExistingNetworkInterface),
                        getUniquePhysicalNetworkLabelValidator(idx, ifaceIdx),
                      ]
                    : [validLabelString, getUniquePhysicalNetworkLabelValidator(idx, ifaceIdx)]
                }
                className={classes.physicalNetworkLabel}
              />
              <RadioFields
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.management`}
                value={iface.management ? 'management' : ''}
                onChange={(value) => updateNetworkInterface(value, 'management', ifaceIdx)}
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                options={[
                  {
                    label: '',
                    value: 'management',
                  },
                ]}
              />
              <RadioFields
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.vmConsole`}
                value={iface.vmConsole ? 'vmConsole' : ''}
                onChange={(value) => updateNetworkInterface(value, 'vmConsole', ifaceIdx)}
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                options={[
                  {
                    label: '',
                    value: 'vmConsole',
                  },
                ]}
              />
              <RadioFields
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.imageLibrary`}
                value={iface.imageLibrary ? 'imageLibrary' : ''}
                onChange={(value) => updateNetworkInterface(value, 'imageLibrary', ifaceIdx)}
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                options={[
                  {
                    label: '',
                    value: 'imageLibrary',
                  },
                ]}
              />
              <RadioFields
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.tunnel`}
                value={iface.tunnel ? 'tunnel' : ''}
                onChange={(value) => updateNetworkInterface(value, 'tunnel', ifaceIdx)}
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                options={[
                  {
                    label: '',
                    value: 'tunnel',
                  },
                ]}
              />
              <RadioFields
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.hostLiveness`}
                value={iface.hostLiveness ? 'hostLiveness' : ''}
                onChange={(value) => updateNetworkInterface(value, 'hostLiveness', ifaceIdx)}
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                options={[
                  {
                    label: '',
                    value: 'hostLiveness',
                  },
                ]}
              />
              {/* Do not show the delete button if only one network interface is available */}
              {config?.networkInterfaces?.length > 1 && (
                <FontAwesomeIcon
                  className={clsx(classes.remove, {
                    [classes.disabled]:
                      (isUsedHostConfig && iface?.physnetName !== '') ||
                      config?.networkInterfaces?.length === 1,
                  })}
                  onClick={() => removeNetworkInterface(ifaceIdx)}
                  disabled={isUsedHostConfig && iface?.physnetName !== ''}
                  solid
                  data-testid="remove-network-interface-button"
                >
                  circle-minus
                </FontAwesomeIcon>
              )}
            </div>
          )
        })}
      </div>
      <div className={classes.tableFooter}>
        <PlusButton
          onClick={addNetworkInterface}
          label="Add Network Interface"
          data-testid="add-network-interface-button"
        />
      </div>
    </div>
  )
}
